import { useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, Chip, CircularProgress, Collapse, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import { BookOpen, Check, ChevronDown, ChevronUp, Copy, ListChecks, RotateCcw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CopilotMarkdown } from './CopilotMarkdown';
import { ProposalCard } from './ProposalCard';
import { copilotApi, FEEDBACK_REASONS, type FeedbackReason } from './copilotApi';
import { errorText, isRetryable, toolLabel } from './copilotText';
import type { CopilotChatMessage } from './useCopilotChat';

/** "Steps": what the assistant looked up, one short line that opens into the full list. */
function Steps({ message }: { message: CopilotChatMessage }) {
  const { t, i18n } = useTranslation('copilot');
  const [open, setOpen] = useState(false);
  const labels = message.tools.map((tool) => toolLabel(tool, t, i18n));
  if (labels.length === 0) return null;
  const working = message.status === 'streaming' && !message.content;
  const last = labels[labels.length - 1];
  return (
    <Box sx={{ mb: 0.75 }}>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{ borderRadius: 1, px: 0.5, py: 0.25, ml: -0.5, color: 'text.secondary', gap: 0.5, typography: 'caption' }}
      >
        {working ? <CircularProgress size={12} aria-hidden /> : <ListChecks size={14} aria-hidden />}
        <span>{working ? last : t('steps.summary', { n: labels.length })}</span>
        {open ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5} sx={{ p: 0, m: 0, mt: 0.5, listStyle: 'none' }} component="ul" aria-label={t('steps.title')}>
          {labels.map((l, i) => (
            <Chip key={`${i}-${l}`} component="li" size="small" variant="outlined" label={l} sx={{ maxWidth: '100%' }} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function Sources({ message }: { message: CopilotChatMessage }) {
  const { t } = useTranslation('copilot');
  if (message.sources.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
        <BookOpen size={12} aria-hidden />
        {t('sources.title')}
      </Typography>
      <Box component="ol" sx={{ m: 0, mt: 0.25, pl: 2.5 }}>
        {message.sources.map((s, i) => (
          <Typography key={`${i}-${s.title}`} component="li" variant="caption" color="text.secondary" sx={{ display: 'list-item' }}>
            {s.origin ? t('sources.item', { title: s.title, origin: s.origin }) : s.title}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function Feedback({ message, onFeedback }: { message: CopilotChatMessage; onFeedback: (v: 'UP' | 'DOWN') => void }) {
  const { t } = useTranslation('copilot');
  const [askReason, setAskReason] = useState(false);
  const [thanked, setThanked] = useState(false);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const id = message.messageId;

  const rate = async (rating: 'up' | 'down', why?: FeedbackReason) => {
    if (!id) return;
    setFailed(false);
    try {
      await copilotApi.feedback(id, rating, why);
      onFeedback(rating === 'up' ? 'UP' : 'DOWN');
      if (rating === 'down' && !why) setAskReason(true);
      if (rating === 'up' || why) { setAskReason(false); setThanked(true); }
    } catch {
      setFailed(true);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      <Stack direction="row" spacing={0.25} alignItems="center">
        <Tooltip title={copied ? t('actions.copied') : t('actions.copy')}>
          <IconButton size="small" onClick={copy} aria-label={t('actions.copy')}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </IconButton>
        </Tooltip>
        {id && (
          <>
            <Tooltip title={t('feedback.up')}>
              <IconButton size="small" onClick={() => rate('up')} aria-label={t('feedback.up')} aria-pressed={message.feedback === 'UP'}
                color={message.feedback === 'UP' ? 'primary' : 'default'}>
                <ThumbsUp size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('feedback.down')}>
              <IconButton size="small" onClick={() => rate('down')} aria-label={t('feedback.down')} aria-pressed={message.feedback === 'DOWN'}
                color={message.feedback === 'DOWN' ? 'primary' : 'default'}>
                <ThumbsDown size={14} />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Box role="status" aria-live="polite" sx={{ typography: 'caption', color: 'text.secondary', pl: 0.5 }}>
          {copied && t('actions.copied')}
          {failed && t('feedback.failed')}
          {thanked && !copied && !failed && t('feedback.thanks')}
        </Box>
      </Stack>
      {askReason && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">{t('feedback.whatWentWrong')}</Typography>
          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5} sx={{ mt: 0.5 }}>
            {FEEDBACK_REASONS.map((r) => (
              <Chip key={r} size="small" label={t(`feedback.reason.${r}`)} onClick={() => rate('down', r)} variant="outlined" />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export function CopilotAnswer({ message, conversationId, onRetry, onFeedback }: {
  message: CopilotChatMessage;
  conversationId: string | null;
  onRetry?: (question: string) => void;
  onFeedback: (v: 'UP' | 'DOWN') => void;
}) {
  const { t } = useTranslation('copilot');
  const thinking = message.status === 'streaming' && !message.content && message.tools.length === 0;
  const finished = message.status === 'done' || message.status === 'stopped';
  return (
    <Box>
      <Steps message={message} />
      {thinking && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
          <CircularProgress size={14} aria-hidden />
          <Typography variant="body2">{t('status.thinking')}</Typography>
        </Stack>
      )}
      {message.content && <CopilotMarkdown content={message.content} />}
      {message.proposals.map((p) => (
        <ProposalCard
          key={p.id} proposal={p} conversationId={conversationId}
          turnDone={message.status === 'done'}
          turnUnfinished={message.status === 'stopped' || message.status === 'error'}
        />
      ))}
      <Sources message={message} />
      {message.status === 'stopped' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{t('status.stopped')}</Typography>
      )}
      {message.status === 'error' && (
        <Alert
          severity={isRetryable(message.error?.code) ? 'warning' : 'info'}
          sx={{ mt: message.content ? 1 : 0, py: 0.25 }}
          action={isRetryable(message.error?.code) && message.question && onRetry ? (
            <Button color="inherit" size="small" startIcon={<RotateCcw size={14} />} onClick={() => onRetry(message.question as string)}>
              {t('actions.retry')}
            </Button>
          ) : undefined}
        >
          {errorText(message.error?.code, t)}
        </Alert>
      )}
      {finished && message.content && <Feedback message={message} onFeedback={onFeedback} />}
    </Box>
  );
}
